import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CircleCheck,
  CircleX,
  Clock,
  Eye,
  History,
  Info,
  KeyRound,
  MoreHorizontal,
  Play,
  RefreshCw,
  TriangleAlert,
  Zap,
} from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ActivityTimeline } from '../activity/ActivityTimeline';
import {
  activityFilterFromParams,
  type ActivityFilterState,
  type ActivityTargets,
} from '../activity/describe';
import { PageContainer } from '../app/AppLayout';
import { isApiError, type Versioned } from '../api/client';
import { useApi } from '../api/context';
import {
  queryKeys,
  useBlueprint,
  useDeployment,
  useDeploymentResources,
  useProviders,
  useRecentRuns,
  useResourceTypeMap,
  useSecrets,
} from '../api/queries';
import {
  isTerminalRunStatus,
  type CreateRunRequest,
  type DeletedVaultCheck,
  type Deployment,
  type Run,
  type SecretsOverview,
} from '../api/types';
import { usePermission } from '../auth/context';
import {
  deploymentStatusPresentation,
  driftPresentation,
  runStatusPresentation,
} from '../components/presentation';
import { useToast } from '../components/toast';
import { ActionButton, ErrorMessage, Loading, StatusBadge } from '../components/ui';
import { useOptionalDock } from '../dock/context';
import { ToolWindowContent } from '../dock/ToolWindowContent';
import { durationMs, formatDateTime, formatDay, formatDuration, formatRelative } from '../lib/format';
import { shortHash } from '../lib/planReview';
import { findPolicyDenial } from '../lib/policyDenial';
import { resolveTriggerSource } from '../lib/descriptions';
import { connectionKey } from '../lib/diagnostics';
import { latestApply, waitingTriggerKeys } from '../lib/triggers';
import { requiredRole } from '../auth/roles';
import { ActivityWindow } from '../runs/ActivityWindow';
import { CancelRunButton } from '../runs/CancelRun';
import { RunChangesCell } from '../runs/RunSummaryTable';
import { MenuButton, type MenuEntry } from '../shell/Menu';
import { WaitingText } from '../networks/components';
import { OriginDetachBlockedCallout } from '../networks/entryPoints/DetachBlocked';
import { detachBlockedDetails } from '../networks/entryPoints/helpers';
import { ConnectRunState, NetworkCard } from '../networks/deployment/NetworkCard';
import { interruption } from '../networks/deployment/helpers';
import { isNetworkRun, useNetworking } from '../networks/shared';
import { strings } from '../strings';
import { useVariablesData, useVariablesScope } from '../variables/useVariablesData';
import { setVariablesView } from '../variables/useVariablesView';
import { parseVarParam, type VariableRef } from '../variables/varParam';
import { VariablesWindow } from '../variables/VariablesWindow';
import { OverviewTab, RunsTab } from './OverviewTab';
import { outOfDatePlan, reviewablePlan, useLatestPlan } from './plans';
import { ApplyDialog, ConfirmDialog, DeleteKeepDialog, DestroyDialog } from './runDialogs';
import { SETTINGS_SECTIONS, type SettingsSection } from './settingsForm';
import { SettingsTab } from './SettingsTab';

const s = strings.deploymentPage;

type OpenDialog = 'apply' | 'destroy' | 'delete' | 'delete-keep' | null;
type PageTab = 'overview' | 'runs' | 'settings' | 'activity';
const PAGE_TABS: readonly PageTab[] = ['overview', 'runs', 'settings', 'activity'];
const ACTIVITY_PARAMS = ['kind', 'secret', 'resource', 'actor'] as const;

function tabFrom(value: string | null): PageTab {
  return PAGE_TABS.includes(value as PageTab) ? (value as PageTab) : 'overview';
}

/** The page's tabs (spec 6.4): arrow keys, Home and End, one tab in the Tab order. */
function PageTabs({
  id,
  active,
  onChange,
}: {
  id: string;
  active: PageTab;
  onChange: (tab: PageTab) => void;
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = PAGE_TABS.indexOf(active);
    const next =
      event.key === 'ArrowRight'
        ? PAGE_TABS[(index + 1) % PAGE_TABS.length]
        : event.key === 'ArrowLeft'
          ? PAGE_TABS[(index - 1 + PAGE_TABS.length) % PAGE_TABS.length]
          : event.key === 'Home'
            ? PAGE_TABS[0]
            : event.key === 'End'
              ? PAGE_TABS[PAGE_TABS.length - 1]
              : null;
    if (!next) return;
    event.preventDefault();
    onChange(next);
    requestAnimationFrame(() => document.getElementById(`${id}-tab-${next}`)?.focus());
  };
  return (
    <div role="tablist" aria-label={s.tabs} className="page-tabs">
      {PAGE_TABS.map((tab) => (
        <button
          key={tab}
          id={`${id}-tab-${tab}`}
          type="button"
          role="tab"
          aria-selected={active === tab}
          aria-controls={`${id}-panel-${tab}`}
          tabIndex={active === tab ? 0 : -1}
          className={`page-tab ${active === tab ? 'is-active' : ''}`}
          onClick={() => onChange(tab)}
          onKeyDown={onKeyDown}
        >
          {s[tab]}
        </button>
      ))}
    </div>
  );
}

/** `/deployments/:id` (spec 6.4, mock m07). */
export function DeploymentDetailPage() {
  const { id = '' } = useParams();
  const deployment = useDeployment(id, {
    refetchInterval: (query) => (query.state.data?.data.activeRunId ? 3000 : false),
  });
  if (deployment.error) return <ErrorMessage error={deployment.error} />;
  if (!deployment.data) return <Loading />;
  // Keyed by deployment: nothing of one deployment (unsaved settings, dialogs, checks) carries over to another.
  return <DeploymentDetail key={deployment.data.data.id} deployment={deployment.data} />;
}

const TONE_CLASS = { danger: 'text-danger', warning: 'text-warning', success: 'text-success' } as const;

interface Banner {
  key: string;
  tone: 'danger' | 'info' | 'warning';
  icon: ReactNode;
  content: ReactNode;
  actions?: ReactNode;
}

function HealthCard({
  label,
  icon,
  value,
  detail,
  tone,
  onClick,
  to,
  testId,
}: {
  label: string;
  icon: ReactNode;
  value: ReactNode;
  detail?: ReactNode;
  tone?: 'danger' | 'warning' | 'success';
  onClick?: () => void;
  to?: string;
  testId: string;
}) {
  const body = (
    <>
      <span className="health-label">
        {icon}
        {label}
      </span>
      <span className={`health-value ${tone ? TONE_CLASS[tone] : ''}`}>{value}</span>
      {detail && <span className="health-detail">{detail}</span>}
    </>
  );
  return to ? (
    <Link className="health-card" to={to} data-testid={testId}>
      {body}
    </Link>
  ) : (
    <button type="button" className="health-card" onClick={onClick} data-testid={testId}>
      {body}
    </button>
  );
}

function DeploymentDetail({ deployment }: { deployment: Versioned<Deployment> }) {
  const api = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useSearchParams();
  const dock = useOptionalDock();
  const tabsId = useId();
  const operate = usePermission('operate');
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [busy, setBusy] = useState(false);
  // Feature spec 17, phase 7: a destroy refused with ORIGIN_DETACH_BLOCKED (shown in the destroy dialog).
  const [destroyBlocked, setDestroyBlocked] = useState<{
    details: ReturnType<typeof detachBlockedDetails>;
    message: string | null;
  } | null>(null);
  const d = deployment.data;
  const tab = tabFrom(search.get('tab'));
  const [visitedSettings, setVisitedSettings] = useState(tab === 'settings');
  if (tab === 'settings' && !visitedSettings) setVisitedSettings(true);
  const section = SETTINGS_SECTIONS.find((key) => key === search.get('section')) ?? null;

  // The Activity tab's filters live in this state and are mirrored into the URL (spec 6.11).
  const urlFilter = activityFilterFromParams(search);
  const urlKey = ACTIVITY_PARAMS.map((key) => urlFilter[key]).join('\n');
  const [activityFilter, setActivityFilterState] = useState<ActivityFilterState>(urlFilter);
  const [ownWrites, setOwnWrites] = useState<readonly string[]>([urlKey]);
  if (!ownWrites.includes(urlKey)) {
    setOwnWrites((written) => [...written.slice(-19), urlKey]);
    setActivityFilterState(urlFilter);
  }
  const setTab = (next: PageTab) => {
    const params = new URLSearchParams(search);
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    if (next !== 'activity') for (const key of ACTIVITY_PARAMS) params.delete(key);
    if (next !== 'settings') params.delete('section');
    setSearch(params, { replace: true });
  };
  const setSection = (key: SettingsSection) => {
    const params = new URLSearchParams(search);
    params.set('tab', 'settings');
    params.set('section', key);
    setSearch(params, { replace: true });
  };
  const setActivityFilter = (filter: ActivityFilterState) => {
    setActivityFilterState(filter);
    setOwnWrites((written) => [...written.slice(-19), ACTIVITY_PARAMS.map((key) => filter[key]).join('\n')]);
    const params = new URLSearchParams(search);
    params.set('tab', 'activity');
    for (const key of ACTIVITY_PARAMS) {
      const value = filter[key];
      if (value) params.set(key, value);
      else params.delete(key);
    }
    setSearch(params, { replace: true });
  };

  const active = !!d.activeRunId;
  const poll = active ? 3000 : false;
  // A run that ends (also one started elsewhere) changes what the page shows: the latest plan, resources,
  // variables, secrets, runs and activity are read again (spec 6.4, code review C8), whatever window is shown.
  const lastRun = useRef(d.activeRunId);
  useEffect(() => {
    const ended = lastRun.current;
    lastRun.current = d.activeRunId;
    if (!ended || ended === d.activeRunId) return;
    void queryClient.invalidateQueries({ queryKey: ['deployment', d.id] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.run(ended) });
    void queryClient.invalidateQueries({ queryKey: ['runs'] });
    void queryClient.invalidateQueries({ queryKey: ['deployments'] });
  }, [d.activeRunId, d.id, queryClient]);
  const blueprint = useBlueprint(d.blueprintId);
  const providers = useProviders();
  const resources = useDeploymentResources(d.id, { refetchInterval: poll });
  const types = useResourceTypeMap();
  const runs = useRecentRuns(d.id, { refetchInterval: poll });
  const secrets = useSecrets(d.id);
  // Only the request that re-read a soft-deleted vault reports it (spec 6.4): keep the newest check seen.
  const [vaultCheck, setVaultCheck] = useState<DeletedVaultCheck | null>(null);
  const seenCheck = [d.vaultCheck, d.vault?.check, secrets.data?.vault.deletedCheck]
    .filter((check): check is DeletedVaultCheck => !!check)
    .sort((x, y) => Date.parse(y.checkedAt) - Date.parse(x.checkedAt))[0];
  if (seenCheck && (!vaultCheck || Date.parse(seenCheck.checkedAt) > Date.parse(vaultCheck.checkedAt)))
    setVaultCheck(seenCheck);
  const latestPlan = useLatestPlan(d.id);
  const activeRun = useQuery({
    queryKey: queryKeys.run(d.activeRunId ?? ''),
    queryFn: () => api.getRun(d.activeRunId!),
    enabled: active,
    refetchInterval: poll,
  });
  const appliedVersion = useQuery({
    queryKey: queryKeys.version(d.blueprintId, d.lastSuccessfulApply?.blueprintVersion ?? 0),
    queryFn: () => api.getVersion(d.blueprintId, d.lastSuccessfulApply!.blueprintVersion),
    enabled: !!d.lastSuccessfulApply,
  });
  const latestVersion = blueprint.data?.data.latestVersion;
  // Feature spec 17: the deployment's runs include its connect and detach runs (network runs); the deployment's own
  // facts (last apply, failed run, plans) come from its own runs, the Network card and the interruption from all.
  const allRuns: Run[] = runs.data?.items ?? [];
  const runList: Run[] = allRuns.filter((r) => !isNetworkRun(r));
  const networking = useNetworking();
  const network = d.network ?? null;
  const lastApply = latestApply(runList);
  const waitingDoc = appliedVersion.data?.content ?? blueprint.data?.data.content;
  const waitingKeys = waitingDoc
    ? waitingTriggerKeys(
        lastApply?.result?.diagnostics,
        waitingDoc,
        types,
        (connection) =>
          resolveTriggerSource(
            {
              doc: waitingDoc,
              types,
              providers: providers.data ?? [],
              overlay: { name: d.name, settings: d.settings },
            },
            connection,
          )?.info.waitsForCode,
      )
    : new Set<string>();
  // The functions of the waiting triggers (connector rule 17, feature spec 16).
  const waitingSources = new Set(
    (waitingDoc?.connections ?? []).filter((c) => waitingKeys.has(connectionKey(c))).map((c) => c.targetId),
  );
  const triggerCount = (waitingDoc?.connections ?? []).filter((c) => c.type === 'triggers').length;

  // ---- Variables window (spec 6.10): all apps, or the one opened from the resources table or `?var=`.
  const [linked] = useState(() => parseVarParam(search.get('var')));
  const variablesScope = useVariablesScope(
    null,
    false,
    linked ? { pinned: true, ids: [linked.resourceId] } : undefined,
  );
  const variables = useVariablesData({ deployment: d, types, scopeIds: variablesScope.effective });
  const [pendingVariable, setPendingVariable] = useState<{ ref: VariableRef; nonce: number } | null>(() =>
    linked ? { ref: linked, nonce: 1 } : null,
  );
  const openVariables = (resourceId: string) => {
    variablesScope.setScope({ pinned: true, ids: [resourceId] });
    setVariablesView(dock, 'variables');
    dock?.show('variables', { focus: true });
  };
  const openSecrets = () => {
    variablesScope.setScope({ pinned: false, ids: null });
    setVariablesView(dock, 'secrets');
    dock?.show('variables', { focus: true });
  };
  // Activity targets (spec 6.11.3): an existing secret's details in the Secrets view, an app's variables.
  const [pendingSecret, setPendingSecret] = useState<{ name: string; nonce: number } | null>(null);
  const activityTargets: ActivityTargets = {
    secrets: new Set((secrets.data?.secrets ?? []).map((x) => x.name)),
    resources: new Set(variables.apps.map((app) => app.resourceId)),
    onShow: (target) => {
      if (target.kind === 'resource') {
        openVariables(target.id);
        return;
      }
      variablesScope.setScope({ pinned: false, ids: null });
      setVariablesView(dock, 'secrets');
      setPendingSecret((current) => ({ name: target.id, nonce: (current?.nonce ?? 0) + 1 }));
      dock?.show('variables', { focus: true });
    },
  };
  // Old links (`?tab=secrets`) show the window's Secrets view; `?var=` shows one variable.
  useEffect(() => {
    const old = search.get('tab');
    if (old !== 'secrets' && !linked) return;
    setVariablesView(dock, linked ? 'variables' : 'secrets');
    dock?.show('variables');
    const next = new URLSearchParams(search);
    if (old === 'secrets') next.delete('tab');
    next.delete('var');
    setSearch(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startRun = async (request: CreateRunRequest) => {
    setBusy(true);
    setDestroyBlocked(null);
    try {
      const run = await api.createRun(d.id, request);
      void queryClient.invalidateQueries({ queryKey: ['deployment', d.id] });
      void queryClient.invalidateQueries({ queryKey: ['runs'] });
      navigate(`/runs/${run.id}`);
    } catch (error) {
      // Feature spec 17, phase 7: a Google Cloud load balancer serves one of its apps; the dialog stays and says why.
      if (request.kind === 'destroy' && isApiError(error, 'ORIGIN_DETACH_BLOCKED')) {
        setDestroyBlocked({ details: detachBlockedDetails(error.problem), message: error.detail ?? null });
        setBusy(false);
        return;
      }
      toast.error(error);
      setBusy(false);
      setDialog(null);
    }
  };

  const remove = async (keepVaultSecrets = false) => {
    setBusy(true);
    try {
      await api.deleteDeployment(d.id, deployment.etag, keepVaultSecrets);
      void queryClient.invalidateQueries({ queryKey: ['deployments'] });
      void queryClient.invalidateQueries({ queryKey: ['blueprints'] });
      toast.show({ tone: 'success', title: s.deleted(d.name) });
      // Unsaved settings of a deleted deployment have nothing to go to: no leave confirmation (spec 6.13).
      navigate(`/blueprints/${d.blueprintId}/deployments`, { state: { discardSettings: true } });
    } catch (error) {
      toast.error(error);
      setBusy(false);
      setDialog(null);
    }
  };

  // ---- header
  const appliedRun = runList.find((r) => r.id === d.lastSuccessfulApply?.runId);
  const placement = d.settings.defaultPlacement;
  const runBlocked = active ? strings.deployments.runActive : null;
  const removable = d.status === 'new' || d.status === 'destroyed';
  // Feature spec 15 5.7: a destroy couldn't delete a namespace vault's secrets (the record stays live).
  const secretsRemain =
    d.status === 'destroyed' && d.vault?.state === 'live' && !!secrets.data?.vault.outlivesVault;
  const remainLabel = secrets.data?.vault.label ?? secrets.data?.vault.type ?? '';
  const remainCount = secrets.data ? secrets.data.secrets.filter((x) => x.kind !== 'unmanaged').length : null;
  const roleReason = operate ? undefined : strings.common.requiresRole(requiredRole('operate'));
  const menu: MenuEntry[] = [
    { kind: 'action', id: 'settings', label: s.editSettings, onSelect: () => setTab('settings') },
    {
      kind: 'action',
      id: 'destroy',
      label: s.destroy,
      disabledReason:
        roleReason ??
        runBlocked ??
        (removable && !secretsRemain ? s.nothingToDestroy : undefined) ??
        (!networking.enabled && network?.mode === 'attached'
          ? strings.networks.deployment.destroyOff(d.name)
          : undefined),
      onSelect: () => setDialog('destroy'),
    },
  ];
  if (removable)
    menu.push({
      kind: 'action',
      id: 'delete',
      label: strings.deployments.delete,
      disabledReason:
        roleReason ??
        runBlocked ??
        (secretsRemain ? strings.vaults.secretsRemain(d.name, remainLabel) : undefined),
      onSelect: () => setDialog('delete'),
    });
  if (secretsRemain)
    menu.push({
      kind: 'action',
      id: 'delete-keep',
      label: strings.vaults.deleteKeep,
      disabledReason: roleReason ?? runBlocked ?? undefined,
      onSelect: () => setDialog('delete-keep'),
    });

  // ---- banners (at most two, most important first)
  const reviewed = active ? null : reviewablePlan(latestPlan.data, d, latestVersion);
  const stale = active || reviewed ? null : outOfDatePlan(latestPlan.data, d, latestVersion);
  const finished = runList.find((r) => isTerminalRunStatus(r.status));
  const overview: SecretsOverview | undefined = secrets.data;
  const banners: Banner[] = [];
  if (secretsRemain && d.vault)
    banners.push({
      key: 'secrets-remain',
      tone: 'danger',
      icon: <CircleX aria-hidden className="size-4 shrink-0" />,
      content: (
        <span data-testid="secrets-remain">
          {strings.vaults.failedDeletion(d.name, remainCount, remainLabel, d.vault.name)}
        </span>
      ),
    });
  if (finished?.status === 'failed') {
    const denial = findPolicyDenial([
      finished.error?.message,
      ...(finished.result?.diagnostics ?? []).filter((x) => x.severity === 'error').map((x) => x.message),
    ]);
    banners.push({
      key: 'failed',
      tone: 'danger',
      icon: <CircleX aria-hidden className="size-4 shrink-0" />,
      content: (
        <>
          <span className="font-semibold">{s.failedRun(strings.status.runKind[finished.kind])}</span>{' '}
          {finished.error ? (
            <>
              <code className="font-mono">{finished.error.code}</code>: {finished.error.message}
            </>
          ) : null}
          {denial && (
            <span className="block">
              {strings.runPage.policyDenial(denial.cloud, denial.policy)} {strings.runPage.policyDenialText}
            </span>
          )}
        </>
      ),
      actions: (
        <Link className="btn btn-sm" to={`/runs/${finished.id}`}>
          {strings.deployments.openRun}
        </Link>
      ),
    });
  }
  const interrupted = networking.enabled ? interruption(d, allRuns) : null;
  if (interrupted)
    banners.push({
      key: 'interrupted',
      tone: 'warning',
      icon: <TriangleAlert aria-hidden className="size-4 shrink-0" />,
      content: (
        <span data-testid="interruption-banner">
          <span className="font-semibold">
            {strings.networks.deployment.interrupted(formatDateTime(interrupted.since))}
          </span>
          {interrupted.run && (
            <>
              {' · '}
              <ConnectRunState run={interrupted.run} />
            </>
          )}
        </span>
      ),
      actions: interrupted.run ? (
        <Link className="btn btn-sm" to={`/runs/${interrupted.run.id}`}>
          {strings.deployments.openRun}
        </Link>
      ) : undefined,
    });
  if (d.status === 'new' && runs.data && runList.length === 0) {
    const vaultFirst =
      overview?.vault.status === 'not-created' && overview.secrets.some((x) => x.kind === 'declared');
    banners.push({
      key: 'next',
      tone: 'info',
      icon: <Info aria-hidden className="size-4 shrink-0" />,
      content: (
        <>
          <span className="font-semibold">{s.nextPlan}</span>
          {vaultFirst && <> {s.nextVault}</>}
        </>
      ),
      actions: (
        <>
          {vaultFirst && (
            <button type="button" className="btn btn-sm" onClick={openSecrets}>
              {s.showSecrets}
            </button>
          )}
          <ActionButton
            permission="operate"
            className="btn-sm"
            onClick={() => void startRun({ kind: 'plan' })}
          >
            {strings.deployments.plan}
          </ActionButton>
        </>
      ),
    });
  }
  if (reviewed) {
    banners.push({
      key: 'plan',
      tone: 'info',
      icon: <Eye aria-hidden className="size-4 shrink-0" />,
      content: (
        <span className="inline-flex flex-wrap items-center gap-1.5" data-testid="plan-banner">
          <span className="font-semibold">{s.planReady}</span>
          {s.planReadyDetail(
            reviewed.blueprintVersion ?? 0,
            formatRelative(reviewed.finishedAt),
            reviewed.createdBy,
          )}
          <RunChangesCell run={reviewed} />
          {s.settingsUnchanged}
        </span>
      ),
      actions: (
        <>
          <Link className="btn btn-sm" to={`/runs/${reviewed.id}`}>
            {s.reviewPlan}
          </Link>
          <ActionButton
            permission="operate"
            variant="primary"
            className="btn-sm"
            onClick={() => setDialog('apply')}
          >
            {s.applyThisPlan}
          </ActionButton>
        </>
      ),
    });
  }
  if (stale) {
    const p = strings.planReview;
    banners.push({
      key: 'plan-stale',
      tone: 'warning',
      icon: <History aria-hidden className="size-4 shrink-0" />,
      content: (
        <span data-testid="plan-stale-banner">
          <span className="font-semibold">
            {s.planOutOfDate(
              stale.plan.blueprintVersion ?? 0,
              formatRelative(stale.plan.finishedAt),
              stale.plan.createdBy,
            )}
          </span>{' '}
          {stale.reasons
            .map((reason) =>
              reason === 'newerVersion'
                ? p.staleReasons.newerVersion(latestVersion ?? 0)
                : p.staleReasons.settingsChanged(
                    shortHash(stale.plan.settingsHash),
                    shortHash(d.settingsHash),
                  ),
            )
            .join('; ')}
          .
        </span>
      ),
      actions: (
        <>
          <Link className="btn btn-sm" to={`/runs/${stale.plan.id}`}>
            {s.reviewPlan}
          </Link>
          <ActionButton
            permission="operate"
            className="btn-sm"
            disabled={busy}
            onClick={() => void startRun({ kind: 'plan' })}
          >
            {p.planAgain}
          </ActionButton>
        </>
      ),
    });
  }
  if (d.needsRefresh || d.needsRecovery) {
    banners.push({
      key: 'refresh',
      tone: 'warning',
      icon: <RefreshCw aria-hidden className="size-4 shrink-0" />,
      content: (
        <>
          {d.needsRefresh && strings.deployments.needsRefresh}
          {d.needsRecovery && <> {strings.deployments.needsRecovery}</>}
        </>
      ),
    });
  }
  if (waitingKeys.size > 0) {
    banners.push({
      key: 'waiting',
      tone: 'warning',
      icon: <Clock aria-hidden className="size-4 shrink-0" />,
      content: <span data-testid="waiting-banner">{strings.triggers.waitingBanner(waitingKeys.size)}</span>,
    });
  }

  // ---- health cards
  const lastApplyRun = runList.find((r) => r.kind === 'apply' && isTerminalRunStatus(r.status));
  const lastRefresh = runList.find((r) => r.kind === 'refresh' && r.status === 'completed');
  const resourceDrift = (lastRefresh?.result?.drift ?? []).filter((x) => x.change !== 'no-change').length;
  const changedSecrets = overview?.summary.changedOutside ?? 0;
  const vaultRecord = d.vault ?? null;
  const vaultCheckFailed = vaultRecord?.state === 'soft-deleted' && !!vaultCheck?.error;
  // A re-read found the soft-deleted vault purged outside Nebisys (`vault` is null then, spec 6.4).
  const vaultPurged = !vaultRecord && vaultCheck?.finding === 'purged' ? vaultCheck : null;

  return (
    <PageContainer full>
      <header className="deployment-header">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">{d.name}</h1>
            <StatusBadge presentation={deploymentStatusPresentation[d.status]} />
            {d.lastSuccessfulApply && d.drift !== 'none' && (
              <StatusBadge presentation={driftPresentation[d.drift]} />
            )}
          </div>
          <p className="mt-1 text-sm text-fg-muted" data-testid="deployment-context">
            {s.blueprint}{' '}
            <Link className="link underline" to={`/blueprints/${d.blueprintId}`}>
              {blueprint.data?.data.name ?? ''}
            </Link>
            {' · '}
            {d.lastSuccessfulApply ? (
              <span title={formatDateTime(d.lastSuccessfulApply.completedAt)}>
                {s.applied(
                  d.lastSuccessfulApply.blueprintVersion,
                  formatRelative(d.lastSuccessfulApply.completedAt),
                )}
                {appliedRun && s.appliedBy(appliedRun.createdBy)}
              </span>
            ) : (
              s.notApplied
            )}
            {latestVersion !== undefined && (
              <>
                {' · '}
                {s.latest(latestVersion)}
                {d.currentBlueprintVersion !== null && d.currentBlueprintVersion < latestVersion && (
                  <span className="badge tone-info ml-1.5">{strings.deployments.updateAvailable}</span>
                )}
              </>
            )}
            {' · '}
            {placement?.provider || placement?.region
              ? s.placement(placement.provider ?? '?', placement.region ?? '?')
              : s.noPlacement}
            {(networking.enabled || network) && (
              <>
                {' · '}
                {network ? (
                  <span data-testid="deployment-network">
                    {strings.networks.deployment.contextNetwork}{' '}
                    {networking.enabled ? (
                      <Link className="link underline" to={`/networks/${network.id}`}>
                        {network.name}
                      </Link>
                    ) : (
                      network.name
                    )}
                    {network.mode === 'retained' && ` · ${strings.networks.deployment.mode.retained}`}
                  </span>
                ) : (
                  strings.networks.deployment.contextStandalone
                )}
              </>
            )}
          </p>
        </div>
        <div className="deployment-actions">
          {!active && (
            <>
              <ActionButton
                permission="operate"
                disabled={busy}
                icon={<Eye aria-hidden className="size-4" />}
                onClick={() => void startRun({ kind: 'plan' })}
              >
                {strings.deployments.plan}
              </ActionButton>
              <ActionButton
                permission="operate"
                variant="primary"
                icon={<Play aria-hidden className="size-4" />}
                onClick={() => setDialog('apply')}
              >
                {s.applyEllipsis}
              </ActionButton>
              <ActionButton
                permission="operate"
                disabledReason={d.lastSuccessfulApply ? null : s.nothingToRefresh}
                icon={<RefreshCw aria-hidden className="size-4" />}
                onClick={() => void startRun({ kind: 'refresh' })}
              >
                {strings.deployments.refresh}
              </ActionButton>
            </>
          )}
          <MenuButton
            buttonContent={<MoreHorizontal aria-hidden className="size-4" />}
            buttonLabel={s.moreActions(d.name)}
            buttonTitle={s.moreActions(d.name)}
            buttonClassName="btn btn-icon"
            menuLabel={s.moreActions(d.name)}
            entries={menu}
            placement="below-end"
          />
        </div>
      </header>

      {active && (
        <div className="callout callout-info mb-3" role="status" data-testid="run-banner">
          <History aria-hidden className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">
            {activeRun.data ? (
              <>
                <span className="font-semibold">
                  {s.runBanner(
                    strings.status.runKind[activeRun.data.kind],
                    activeRun.data.status === 'pending' ? 'pending' : 'running',
                  )}
                </span>
                {activeRun.data.progress && activeRun.data.progress.total > 0 && (
                  <>
                    {' '}
                    · {activeRun.data.progress.completed}/{activeRun.data.progress.total}
                  </>
                )}
                {' · '}
                {activeRun.data.status === 'pending' && activeRun.data.waitingFor ? (
                  <WaitingText waitingFor={activeRun.data.waitingFor} />
                ) : (
                  s.runStarted(
                    formatRelative(activeRun.data.startedAt ?? activeRun.data.createdAt),
                    activeRun.data.createdBy,
                  )
                )}
              </>
            ) : (
              strings.deployments.runActive
            )}
          </span>
          <Link className="btn btn-sm" to={`/runs/${d.activeRunId}`}>
            {strings.deployments.openRun}
          </Link>
          {activeRun.data && !isTerminalRunStatus(activeRun.data.status) && (
            <CancelRunButton run={activeRun.data} label={s.cancelEllipsis} small />
          )}
        </div>
      )}

      {banners.length > 0 && (
        <div aria-label={s.banners} role="group" className="mb-3 space-y-2">
          {banners.slice(0, 2).map((banner) => (
            <div
              key={banner.key}
              className={`callout callout-${banner.tone}`}
              data-testid={`banner-${banner.key}`}
            >
              {banner.icon}
              <div className="min-w-0 flex-1">{banner.content}</div>
              {banner.actions && <div className="flex shrink-0 flex-wrap gap-2">{banner.actions}</div>}
            </div>
          ))}
        </div>
      )}

      <div role="group" aria-label={s.health} className="health-cards">
        <HealthCard
          testId="health-last-apply"
          label={s.lastApply}
          icon={<CircleCheck aria-hidden className="size-3.5" />}
          value={lastApplyRun ? runStatusPresentation[lastApplyRun.status].label : s.neverApplied}
          tone={lastApplyRun?.status === 'failed' ? 'danger' : undefined}
          detail={
            lastApplyRun
              ? `${formatRelative(lastApplyRun.finishedAt)} · ${formatDuration(durationMs(lastApplyRun.startedAt, lastApplyRun.finishedAt))} · v${lastApplyRun.blueprintVersion ?? '?'}`
              : undefined
          }
          to={lastApplyRun ? `/runs/${lastApplyRun.id}` : undefined}
          onClick={() => setTab('runs')}
        />
        <HealthCard
          testId="health-drift"
          label={s.drift}
          icon={<TriangleAlert aria-hidden className="size-3.5" />}
          value={
            d.drift === 'detected'
              ? changedSecrets > 0
                ? s.driftSecrets(changedSecrets)
                : s.driftDetected
              : d.drift === 'none'
                ? s.noDrift
                : s.notChecked
          }
          tone={d.drift === 'detected' ? 'warning' : undefined}
          detail={
            lastRefresh
              ? `${s.refreshAt(formatRelative(lastRefresh.finishedAt))} · ${resourceDrift ? s.resourcesDrifted(resourceDrift) : s.resourcesInSync}`
              : s.noRefresh
          }
          onClick={openSecrets}
        />
        <HealthCard
          testId="health-vault"
          label={s.vault}
          icon={<KeyRound aria-hidden className="size-3.5" />}
          value={
            vaultRecord?.state === 'soft-deleted'
              ? s.vaultSoftDeleted(formatDay(vaultRecord.purgeAfter))
              : overview?.vault.status === 'ready'
                ? s.vaultReady(overview.vault.label ?? strings.variables.locationLabels.azureKeyVault)
                : overview?.vault.status === 'not-created'
                  ? s.vaultNotCreated
                  : overview?.vault.status === 'store'
                    ? (overview.vault.label ?? strings.common.dash)
                    : overview?.vault.status === 'none'
                      ? s.vaultNone
                      : strings.common.dash
          }
          tone={(overview?.summary.missingValues ?? 0) > 0 || vaultCheckFailed ? 'warning' : undefined}
          detail={
            vaultCheckFailed ? (
              s.vaultCouldNotCheck
            ) : vaultPurged ? (
              <span data-testid="vault-purged" title={formatDateTime(vaultPurged.checkedAt)}>
                {s.vaultPurged(formatRelative(vaultPurged.checkedAt))}
              </span>
            ) : (overview?.summary.missingValues ?? 0) > 0 ? (
              s.vaultMissing(overview!.summary.missingValues)
            ) : overview?.vault.status === 'store' ? (
              s.vaultStoreHelp
            ) : (overview?.vault.name ?? vaultRecord?.name) ? (
              <span className="font-mono">{overview?.vault.name ?? vaultRecord?.name}</span>
            ) : undefined
          }
          onClick={
            vaultCheckFailed
              ? () => {
                  setSection('vault');
                  requestAnimationFrame(() =>
                    requestAnimationFrame(() =>
                      document
                        .querySelector('[data-settings-section="vault"]')
                        ?.scrollIntoView?.({ block: 'start' }),
                    ),
                  );
                }
              : openSecrets
          }
        />
        <HealthCard
          testId="health-triggers"
          label={s.triggers}
          icon={<Zap aria-hidden className="size-3.5" />}
          value={
            waitingKeys.size > 0
              ? s.triggersWaiting(waitingKeys.size)
              : triggerCount > 0
                ? s.noneWaiting
                : s.noTriggers
          }
          tone={waitingKeys.size > 0 ? 'warning' : undefined}
          detail={waitingKeys.size > 0 ? s.triggersHelp : undefined}
          onClick={() => {
            setTab('overview');
            requestAnimationFrame(() => document.getElementById('deploy-boxes')?.scrollIntoView?.());
          }}
        />
      </div>

      <PageTabs id={tabsId} active={tab} onChange={setTab} />
      {tab === 'overview' && (
        <div
          id={`${tabsId}-panel-overview`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-overview`}
          className="page-tabpanel"
        >
          {(networking.enabled || network) && (
            <NetworkCard
              deployment={d}
              runs={allRuns}
              onApply={() => setDialog('apply')}
              onOpenSettings={() => setSection('network')}
            />
          )}
          <OverviewTab
            deployment={d}
            resources={resources.data}
            resourcesError={resources.error}
            types={types}
            variables={variables}
            waitingSources={waitingSources}
            active={active}
            onOpenVariables={openVariables}
            onShowRuns={() => setTab('runs')}
          />
        </div>
      )}
      {tab === 'runs' && (
        <div
          id={`${tabsId}-panel-runs`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-runs`}
          className="page-tabpanel"
        >
          <RunsTab deployment={d} active={active} />
        </div>
      )}
      {visitedSettings && (
        <div
          id={`${tabsId}-panel-settings`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-settings`}
          className="page-tabpanel"
          hidden={tab !== 'settings'}
        >
          {blueprint.data && providers.data ? (
            <SettingsTab
              deployment={deployment}
              doc={blueprint.data.data.content}
              providers={providers.data}
              resources={resources.data ?? []}
              types={types}
              vaultType={overview?.vault.type}
              vaultOverview={overview?.vault ?? null}
              vaultPurged={vaultPurged}
              activeApply={activeRun.data?.kind === 'apply' ? activeRun.data : null}
              section={section}
              onSection={setSection}
              onSaved={(saved) => {
                queryClient.setQueryData(queryKeys.deployment(d.id), saved);
                void queryClient.invalidateQueries({ queryKey: ['deployment', d.id] });
                void queryClient.invalidateQueries({ queryKey: ['deployments'] });
              }}
            />
          ) : (
            <Loading />
          )}
        </div>
      )}
      {tab === 'activity' && (
        <div
          id={`${tabsId}-panel-activity`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-activity`}
          className="page-tabpanel"
        >
          <ActivityTimeline
            deployment={d}
            filter={activityFilter}
            onFilterChange={setActivityFilter}
            resources={(resources.data ?? []).map((r) => r.resourceId)}
            targets={activityTargets}
          />
        </div>
      )}

      {dialog === 'apply' && latestVersion !== undefined && (
        <ApplyDialog
          deployment={deployment}
          latestVersion={latestVersion}
          busy={busy}
          onClose={() => setDialog(null)}
          onSubmit={(request) => void startRun(request)}
        />
      )}
      {dialog === 'destroy' && (
        <DestroyDialog
          deployment={d}
          resources={resources.data ?? []}
          types={types}
          vaultType={overview?.vault.type}
          vaultOverview={overview ?? null}
          busy={busy}
          onClose={() => {
            setDestroyBlocked(null);
            setDialog(null);
          }}
          onConfirm={() => void startRun({ kind: 'destroy' })}
          blocked={
            destroyBlocked ? (
              <OriginDetachBlockedCallout details={destroyBlocked.details} message={destroyBlocked.message} />
            ) : null
          }
          networkNote={
            networking.enabled && network?.mode === 'attached'
              ? strings.networks.deployment.destroyDetach(d.name, network.name)
              : null
          }
        />
      )}
      {dialog === 'delete-keep' && d.vault && (
        <DeleteKeepDialog
          deployment={d.name}
          text={strings.vaults.deleteKeepText(remainCount, remainLabel, d.vault.name, d.vault.region)}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={() => void remove(true)}
        />
      )}
      {dialog === 'delete' && (
        <ConfirmDialog
          title={strings.deployments.delete}
          message={strings.deployments.deleteConfirm(d.name)}
          confirmLabel={strings.common.delete}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={() => void remove()}
        />
      )}
      <ToolWindowContent window="variables" badge={variables.badge}>
        <VariablesWindow
          context="deployment"
          data={variables}
          types={types}
          blueprintId={d.blueprintId}
          deployment={d}
          scope={variablesScope.scope}
          effectiveScope={variablesScope.effective}
          onScopeChange={variablesScope.setScope}
          pending={pendingVariable}
          onPendingHandled={() => setPendingVariable(null)}
          pendingSecret={pendingSecret}
          parameters={blueprint.data ? (blueprint.data.data.content.parameters ?? {}) : undefined}
        />
      </ToolWindowContent>
      <ToolWindowContent window="activity" badge={{ live: active }}>
        <ActivityWindow
          runId={d.activeRunId ?? null}
          deployment={d}
          resources={(resources.data ?? []).map((r) => r.resourceId)}
          targets={activityTargets}
        />
      </ToolWindowContent>
    </PageContainer>
  );
}
